import { z } from 'zod';

import type { MatchesSchema } from '@/shared/api/contract';

/**
 * Zobowiązania — model publiczny modułu.
 *
 * Zobowiązanie ma **dwie niezależne osie** (spec sek. 3): zrobione/niezrobione oraz
 * aktualne/nieaktualne. Druga jest antidotum na gnicie — pozwala zamknąć coś, co
 * przestało mieć sens, bez udawania, że zostało wykonane. Stąd `closeReason`:
 * to nie ozdoba, tylko odpowiedź na pytanie „czemu tego już nie ma na liście".
 */

/** Gdzie w cyklu życia jest zobowiązanie (decyzje 21–22). */
export const commitmentStateSchema = z.enum([
  /** Czeka na Surta — „śpi". */
  'dormant',
  /** Osierocone: źródło zapalnika zamknięto jako nieaktualne. To **draugr**. */
  'orphaned',
  'active',
  'snoozed',
  'done',
  'obsolete',
]);

export type CommitmentState = z.infer<typeof commitmentStateSchema>;

export const commitmentCloseReasonSchema = z.enum(['done', 'obsolete']).nullable();

export type CommitmentCloseReason = 'done' | 'obsolete';

/**
 * Który wycinek listy pobieramy. Filtr jest **serwerowy**, nie kliencki — element bywa
 * gospodarzem setek zamkniętych zobowiązań, a ściąganie ich po to, żeby je ukryć,
 * byłoby płaceniem za dane, których nikt nie zobaczy.
 */
export const commitmentScopeSchema = z.enum(['open', 'active', 'dormant', 'closed', 'all']);

export type CommitmentScope = z.infer<typeof commitmentScopeSchema>;

/**
 * Skąd wzięło się zadanie — `null`, gdy założył je człowiek.
 *
 * ⭐⭐ To **jedyna** rzecz, którą zwykły użytkownik widzi z kampanii (decyzja 109): sam ekran
 * kampanii jest za polityką architekta, ale zadanie, które z niej spadło, nie może być bez
 * pochodzenia. „Skąd wzięło się MOJE zadanie" i „jak idzie cudza kampania" to dwa różne pytania.
 *
 * ⛔ Kontrakt świadomie **nie niesie odsyłacza** do ekranu kampanii — dla nie-architekta
 * prowadziłby w 403, a drzwi, które i tak są zamknięte, się nie rysuje.
 */
export const commitmentOriginSchema = z.object({
  campaignId: z.number(),
  /** Treść użytkownika — przechowywana tak, jak wpisana, i nietłumaczona (spec 4C). */
  campaignTitle: z.string(),
});

export type CommitmentOrigin = z.infer<typeof commitmentOriginSchema>;

export const commitmentSummarySchema = z.object({
  id: z.number(),
  hostElementId: z.number(),
  hostElementTitle: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  /** Oś pierwsza: zrobione / niezrobione. */
  done: z.boolean(),
  /** Oś druga: aktualne / nieaktualne — antidotum na gnicie (spec sek. 3). */
  valid: z.boolean(),
  closeReason: commitmentCloseReasonSchema,
  state: commitmentStateSchema,
  iterationNumber: z.number().nullable(),
  surtSourceCommitmentId: z.number().nullable(),
  surtSourceTitle: z.string().nullable(),
  firedAtUtc: z.string().nullable(),
  orphanedAtUtc: z.string().nullable(),
  snoozedUntilUtc: z.string().nullable(),
  createdByUserId: z.number().nullable(),
  createdAtUtc: z.string(),
  /**
   * Czy TEN użytkownik może zamknąć TO zobowiązanie (spec 9.2).
   *
   * ⚠ Liczy to serwer i tak musi być: własność bywa **dziedziczona po `has`**, więc
   * front nie ma z czego jej wyprowadzić. Ukrycie przycisku to wygoda, nie kontrola
   * dostępu — tej broni API. Decyzja 63 mówi wprost, że przy większości wierszy akcji
   * nie będzie i **tak ma być**: panel jest informacyjny dla cudzych, wykonawczy dla swoich.
   */
  canClose: z.boolean(),
  origin: commitmentOriginSchema.nullable(),
});

export type CommitmentSummary = z.infer<typeof commitmentSummarySchema>;

/**
 * Rodzaj ogniwa historii. Historia zobowiązania to **dziennik dopisywany na koniec**,
 * nigdy nadpisywany (decyzja 22) — ponowne otwarcie nie kasuje zamknięcia, tylko
 * dokłada kolejne ogniwo.
 */
export const commitmentEventKindSchema = z.enum([
  'created',
  'fired',
  'closed',
  'reopened',
  'unfired',
  'orphaned',
  'orphanCleared',
  'snoozed',
  'edited',
]);

export type CommitmentEventKind = z.infer<typeof commitmentEventKindSchema>;

export const commitmentEventSchema = z.object({
  id: z.number(),
  kind: commitmentEventKindSchema,
  /** Zobowiązanie, które wywołało to zdarzenie — ogniwo łańcucha Surtów. */
  causeCommitmentId: z.number().nullable(),
  causeTitle: z.string().nullable(),
  actorUserId: z.number().nullable(),
  actorName: z.string().nullable(),
  iterationNumber: z.number().nullable(),
  atUtc: z.string(),
  detailKey: z.string().nullable(),
});

export type CommitmentEvent = z.infer<typeof commitmentEventSchema>;

/**
 * Właściciel w skrócie — tyle, ile trzeba, żeby powiedzieć „to wylądowało u kogoś".
 *
 * ⚠ To **nie jest** `OwnerView` z modułu `elements`: tamten niesie proweniencję
 * (`isLocal`, `sourceElementTitle`), bo odpowiada na pytanie „skąd ten właściciel".
 * Tutaj pytanie jest inne — „kto to dostał" — więc i kształt jest inny, łącznie
 * z nazwami pól. Zbieżność nazwy typu jest przypadkowa i nie wolno ich mieszać.
 */
export const ownerRefSchema = z.object({
  kind: z.enum(['user', 'team']),
  id: z.number(),
  name: z.string(),
});

export type OwnerRef = z.infer<typeof ownerRefSchema>;

/**
 * Zobowiązanie ruszone **skutkiem ubocznym** cudzej akcji: obudzone, uśpione z powrotem
 * albo osierocone.
 *
 * ⭐ To jest ta „krawędź do przyszłości, która krzepnie" — sedno produktu. Zamknięcie
 * jednego zadania budzi cudze zobowiązania u innych ludzi i **człowiek zamykający musi
 * to zobaczyć**, inaczej cała mechanika Surtów jest niewidzialna.
 */
export const triggeredCommitmentSchema = z.object({
  commitmentId: z.number(),
  title: z.string(),
  hostElementId: z.number(),
  hostElementTitle: z.string(),
  owners: z.array(ownerRefSchema),
  /** Głębokość łańcucha: 1 = obudzone wprost, 2 = obudzone przez obudzone. */
  depth: z.number(),
  state: commitmentStateSchema,
});

export type TriggeredCommitment = z.infer<typeof triggeredCommitmentSchema>;

export const commitmentDetailSchema = z.object({
  commitment: commitmentSummarySchema,
  history: z.array(commitmentEventSchema),
  /** Co się stanie po zamknięciu jako zrobione — podgląd bez zapisu (spec sek. 8). */
  wouldFire: z.array(triggeredCommitmentSchema),
});

export type CommitmentDetail = z.infer<typeof commitmentDetailSchema>;

/**
 * Zapowiedź odcięcia iteracji — **bez zapisu** (spec sek. 7: „uruchomienie odpali N zadań
 * do tych właścicieli — kontynuować?"). ⚠ `wouldFire` bierze też **zaległości** z poprzednich
 * paczek (`<= next`, decyzja 91) — dlatego podgląd nie jest ozdobą, tylko warunkiem uczciwości.
 */
export const iterationPreviewSchema = z.object({
  elementId: z.number(),
  /** Ostatnia odcięta iteracja; 0 = jeszcze żadnej. */
  currentNumber: z.number(),
  nextNumber: z.number(),
  wouldFire: z.array(triggeredCommitmentSchema),
});

export type IterationPreview = z.infer<typeof iterationPreviewSchema>;

/** Skutek odcięcia iteracji. Cofnięcia nie ma (decyzja 91) — odwrotem jest zamykanie po jednym. */
export const iterationStartedSchema = z.object({
  elementId: z.number(),
  number: z.number(),
  fired: z.array(triggeredCommitmentSchema),
});

export type IterationStarted = z.infer<typeof iterationStartedSchema>;

export const closeOutcomeSchema = z.object({
  commitment: commitmentSummarySchema,
  /** Obudzone tym zamknięciem (`done`). */
  fired: z.array(triggeredCommitmentSchema),
  /** Osierocone tym zamknięciem (`obsolete`) — decyzja 21: system NIE zamyka ich sam. */
  orphaned: z.array(triggeredCommitmentSchema),
});

export type CloseOutcome = z.infer<typeof closeOutcomeSchema>;

export const reopenOutcomeSchema = z.object({
  commitment: commitmentSummarySchema,
  /** Uśpione z powrotem, bo warunek przestał być spełniony (decyzja 22). */
  sentBackToSleep: z.array(triggeredCommitmentSchema),
  /** Draugry, które przestały nimi być. */
  orphansCleared: z.array(triggeredCommitmentSchema),
});

export type ReopenOutcome = z.infer<typeof reopenOutcomeSchema>;

/**
 * Zobowiązanie obudzone **moją** robotą — soczewka „co uruchomiłem" (spec sek. 8).
 *
 * ⚠ Jedyna soczewka **niezależna od własności**: pokazuje skutki mojej akcji także na
 * cudzych elementach. O to w niej właśnie chodzi — odpowiada na „co po mnie zostało",
 * a nie na „co mam do zrobienia".
 */
export const launchedCommitmentSchema = z.object({
  commitment: commitmentSummarySchema,
  launchedAtUtc: z.string(),
  causeCommitmentId: z.number().nullable(),
  causeTitle: z.string().nullable(),
});

export type LaunchedCommitment = z.infer<typeof launchedCommitmentSchema>;

/** Rodzaj wiadomości w skrzynce (decyzje 21–22). */
export const noticeKindSchema = z.enum([
  /** Źródło zapalnika zamknięto jako nieaktualne — zadanie osierocone. */
  'deadSurt',
  /** Źródło otwarto ponownie, więc obudzone wróciło do snu. */
  'backToSleep',
]);

export type NoticeKind = z.infer<typeof noticeKindSchema>;

export const dashboardNoticeSchema = z.object({
  id: z.number(),
  kind: noticeKindSchema,
  commitment: commitmentSummarySchema,
  causeCommitmentId: z.number().nullable(),
  causeTitle: z.string().nullable(),
  createdAtUtc: z.string(),
});

export type DashboardNotice = z.infer<typeof dashboardNoticeSchema>;

/**
 * Pulpit — pięć grup z serwera, sześć kafli na ekranie.
 *
 * ⚠ Rozbieżność jest **zamierzona i wynika z danych, nie z niedoróbki**: `dormant` niesie
 * wszystko, co czeka na zapalnik, **razem z osieroconymi** (tak mówi SQL `MyDormant`).
 * Ekran rozdziela je na „czeka na surt" i „martwe surty", bo to dwie różne sytuacje:
 * pierwsza jest normalna, druga wymaga decyzji człowieka (decyzja 21).
 *
 * Podział `freshlyFired` / `hanging` liczy serwer i jest **strukturalny**, nie czasowy:
 * „obudzone zapalnikiem" kontra „aktywne od urodzenia".
 */
export const dashboardSchema = z.object({
  freshlyFired: z.array(commitmentSummarySchema),
  hanging: z.array(commitmentSummarySchema),
  dormant: z.array(commitmentSummarySchema),
  launched: z.array(launchedCommitmentSchema),
  notices: z.array(dashboardNoticeSchema),
});

export type Dashboard = z.infer<typeof dashboardSchema>;

export interface CreateCommitmentInput {
  hostElementId: number;
  title: string;
  body?: string | null;
  iterationNext?: boolean;
  iterationNumber?: number | null;
  surtSourceCommitmentId?: number | null;
}

export interface CloseCommitmentInput {
  id: number;
  reason: CommitmentCloseReason;
  /** Do unieważnienia listy gospodarza — odpowiedź niesie samo zobowiązanie, nie listę. */
  hostElementId: number;
}

export interface ReopenCommitmentInput {
  id: number;
  hostElementId: number;
}

// ── Spinacz z kontraktem API (decyzja 74) ───────────────────────────────────

/**
 * Każda pozycja to jedna para „nasz typ ↔ schemat z dokumentu OpenAPI". `satisfies`
 * zgłasza błąd, gdy `Matches` nie wychodzi na `true`, a treść błędu mówi, w którą
 * stronę jest rozjazd.
 *
 * ⚠ Nazwy po obu stronach nie zawsze są lustrzane (`CommitmentEventDto`,
 * `CommitmentEventKindDto`) — API dodaje przyrostek tam, gdzie odróżnia DTO od encji.
 * Para wiąże je jawnie, zamiast wymuszać wspólne nazewnictwo.
 */
/**
 * Obciążenie zespołu (decyzja 82) — <b>wyłącznie odczyt</b>.
 *
 * ⭐ Proweniencja („czyj to wiersz") nie potrzebuje tu żadnego znacznika, bo zakresem jest
 * **pojedynczy zespół**: wiadomo, kogo dotyczy, bo o niego pytano. Znacznik byłby potrzebny
 * dopiero tam, gdzie jednym wywołaniem pyta się o użytkownika **i** jego zespoły — jak pulpit.
 */
export const teamWorkloadElementSchema = z.object({
  elementId: z.number(),
  title: z.string(),
  active: z.number(),
  sleeping: z.number(),
  draugr: z.number(),
});

export type TeamWorkloadElement = z.infer<typeof teamWorkloadElementSchema>;

export const teamWorkloadSchema = z.object({
  teamId: z.number(),
  teamName: z.string(),
  elements: z.number(),
  active: z.number(),
  sleeping: z.number(),
  draugr: z.number(),
  /** ⭐ Nie cała lista — „gdzie zaglądnąć najpierw", nie spis zawartości zespołu. */
  hottest: z.array(teamWorkloadElementSchema),
});

export type TeamWorkload = z.infer<typeof teamWorkloadSchema>;

export const commitmentsContract = {
  TeamWorkload: true satisfies MatchesSchema<TeamWorkload, 'TeamWorkload'>,
  TeamWorkloadElement: true satisfies MatchesSchema<TeamWorkloadElement, 'TeamWorkloadElement'>,
  CommitmentSummary: true satisfies MatchesSchema<CommitmentSummary, 'CommitmentSummary'>,
  CommitmentState: true satisfies MatchesSchema<CommitmentState, 'CommitmentState'>,
  CommitmentScope: true satisfies MatchesSchema<CommitmentScope, 'CommitmentScope'>,
  CommitmentEvent: true satisfies MatchesSchema<CommitmentEvent, 'CommitmentEventDto'>,
  CommitmentEventKind: true satisfies MatchesSchema<CommitmentEventKind, 'CommitmentEventKindDto'>,
  TriggeredCommitment: true satisfies MatchesSchema<TriggeredCommitment, 'TriggeredCommitment'>,
  CommitmentDetail: true satisfies MatchesSchema<CommitmentDetail, 'CommitmentDetail'>,
  CommitmentOrigin: true satisfies MatchesSchema<CommitmentOrigin, 'CommitmentOriginInfo'>,
  IterationPreview: true satisfies MatchesSchema<IterationPreview, 'IterationPreview'>,
  IterationStarted: true satisfies MatchesSchema<IterationStarted, 'IterationStarted'>,
  CloseOutcome: true satisfies MatchesSchema<CloseOutcome, 'CloseOutcome'>,
  ReopenOutcome: true satisfies MatchesSchema<ReopenOutcome, 'ReopenOutcome'>,
  OwnerRef: true satisfies MatchesSchema<OwnerRef, 'OwnerRef'>,
  Dashboard: true satisfies MatchesSchema<Dashboard, 'Dashboard'>,
  DashboardNotice: true satisfies MatchesSchema<DashboardNotice, 'DashboardNotice'>,
  NoticeKind: true satisfies MatchesSchema<NoticeKind, 'CommitmentNoticeKindDto'>,
  LaunchedCommitment: true satisfies MatchesSchema<LaunchedCommitment, 'LaunchedCommitment'>,
} as const;
